<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

    <h4 class="sa-core-color fw-semibold mb-2 d-flex align-items-center flex-wrap gap-2">
        <span><i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}</span>
        <button type="button" class="btn btn-sm btn-outline-secondary ms-auto" title="Refresh" @click="fetchStatistics()">
            <i class="fa fa-refresh sa-core-color"></i>
        </button>
    </h4>

    <!-- Totals -->
    <div class="row g-2 mb-2">
        <div class="col-6 col-md-3">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-2">
                    <div class="stat-label sa-core-color">Total users</div>
                    <div class="stat-value sa-core-color">{{ formatNumber(stats.totals.total) }}</div>
                    <div class="small sa-core-color">{{ formatNumber(stats.totals.active) }} active · {{ formatNumber(stats.totals.inactive) }} inactive</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-md-3">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-2">
                    <div class="stat-label sa-core-color">Online now</div>
                    <div class="stat-value sa-core-color"><i class="fa fa-circle online-dot me-1"></i>{{ formatNumber(stats.totals.online) }}</div>
                    <div class="small sa-core-color">{{ percent(stats.totals.online, stats.totals.total) }}% of all users</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-md-3">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-2">
                    <div class="stat-label sa-core-color">New this month</div>
                    <div class="stat-value sa-core-color">+{{ formatNumber(stats.totals.new_this_month) }}</div>
                    <div class="small sa-core-color">Since the 1st</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-md-3">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-2">
                    <div class="stat-label sa-core-color">Countries</div>
                    <div class="stat-value sa-core-color">{{ knownCountries.length }}</div>
                    <div class="small sa-core-color">
                        <template v-if="knownCountries.length">Top: {{ $getFlag(knownCountries[0].code) }} {{ $getCountryName(knownCountries[0].code) }}</template>
                        <template v-else>-</template>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-2">
        <!-- Users per country -->
        <div class="col-lg-5">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-0">
                    <div class="p-2"><strong class="sa-core-color"><i class="fa fa-globe me-1"></i>Users per country</strong></div>
                    <div class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Country</th>
                                    <th class="text-end">Users</th>
                                    <th class="text-end hide-sm-td">Online</th>
                                    <th style="width: 35%">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="stats.by_country.length === 0">
                                    <td colspan="4" class="text-center text-muted">No users yet.</td>
                                </tr>
                                <tr v-for="row in stats.by_country" :key="row.code || 'none'">
                                    <td class="text-nowrap">
                                        <template v-if="row.code">
                                            <span class="me-1">{{ $getFlag(row.code) }}</span>{{ $getCountryName(row.code) }}
                                        </template>
                                        <span v-else class="text-muted">Not set</span>
                                    </td>
                                    <td class="text-end">{{ formatNumber(row.users) }}</td>
                                    <td class="text-end hide-sm-td">{{ formatNumber(row.online) }}</td>
                                    <td>
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="progress flex-grow-1" style="height: 6px">
                                                <div class="progress-bar bg-info" :style="{ width: percent(row.users, stats.totals.total) + '%' }"></div>
                                            </div>
                                            <small class="text-nowrap">{{ percent(row.users, stats.totals.total) }}%</small>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Users per client -->
        <div class="col-lg-7">
            <div class="card glass bg-sa-card h-100">
                <div class="card-body bg-sa-card-body p-0">
                    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 p-2">
                        <strong class="sa-core-color"><i class="fa fa-building me-1"></i>Users per client</strong>
                        <input v-model="companySearch" type="search" class="form-control form-control-sm w-auto" placeholder="Search company..." />
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Company</th>
                                    <th class="hide-sm-td">Country</th>
                                    <th class="text-end">Users</th>
                                    <th class="text-end hide-sm-td">Active</th>
                                    <th class="text-end">Online</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="filteredCompanies.length === 0">
                                    <td colspan="6" class="text-center text-muted">No companies found.</td>
                                </tr>
                                <tr v-for="(row, index) in filteredCompanies" :key="row.company_id">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ row.company_name }}</td>
                                    <td class="hide-sm-td text-nowrap">
                                        <template v-if="row.country">
                                            <span class="me-1">{{ $getFlag(row.country) }}</span>{{ $getCountryName(row.country) }}
                                        </template>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                    <td class="text-end">{{ formatNumber(row.users) }}</td>
                                    <td class="text-end hide-sm-td">{{ formatNumber(row.active) }}</td>
                                    <td class="text-end text-nowrap">
                                        <i v-if="row.online > 0" class="fa fa-circle online-dot me-1"></i>{{ formatNumber(row.online) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PageStatus from "../../../components/admin/PageStatus.vue";
    import userStatisticsMixin from "./mixins/userStatisticsMixin.js";
    import axios from "axios";

    const API = "/clients/api_user_statistics.php";

    export default {
        name: "UserStatistics",
        mixins: [userStatisticsMixin], // UserStatistics' own (no add/edit table, so no tableMixin)
        components: {
            PageStatus,
        },
        data() {
            return {
                stats: this.getDefaultStats(),
                companySearch: "",
                tableData: this.getDefaultTableData(),
                defaultAlert: this.getDefaultAlert(),
                pageIcon: "fa fa-bar-chart", // same icon as this page in the sidebar
            };
        },
        async created() {
            await this.fetchStatistics();
            this.$checkIfCanFetchLocation(); // Only applies if location is enabled for the user
        },
        computed: {
            pageTitle() {
                return "User Statistics";
            },
            // Countries with a code (for the "Countries" tile)
            knownCountries() {
                return this.stats.by_country.filter((c) => c.code);
            },
            filteredCompanies() {
                const q = this.companySearch.trim().toLowerCase();
                return q
                    ? this.stats.by_company.filter((c) => String(c.company_name || "").toLowerCase().includes(q))
                    : this.stats.by_company;
            },
        },
        methods: {
            getDefaultStats() {
                return {
                    totals: { total: 0, active: 0, inactive: 0, online: 0, new_this_month: 0 },
                    by_country: [],
                    by_company: [],
                };
            },
            getDefaultTableData() {
                return {
                    isVisible: false,
                    loaderMessage: this.$loaderMessages.loaderMessageDefault,
                    loaderValue: null, // PageStatus shows it after the message
                };
            },
            getDefaultAlert() {
                return {
                    alertMessage: "",
                    alertstatus: "",
                    showAlert: "",
                    alertType: "",
                    button_text: "",
                    pageOrModal: "",
                };
            },
            fetchStatistics() {
                // Set directly ($showOrHideLoader toggles)
                this.tableData.loaderMessage = this.$loaderMessages.loaderMessageDefault;
                this.tableData.isVisible = true;

                return axios
                    .get(`${API}?action=fetchStatistics`, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleFetchSuccess(response);
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error))
                    .finally(() => {
                        this.tableData.isVisible = false;
                    });
            },
        },
    };
</script>